import { authService } from "@/libs/auth/services/AccountService";
import { config } from "@/utils/config";
import { getLogger } from "@/utils/logger";
import { io, Socket } from "socket.io-client";
import {
  ChatClientToServerEvents,
  ChatServerToClientEvents,
  DriverClientToServerEvents,
  DriverServerToClientEvents,
} from "../types";

const log = getLogger("app:libs:api:websockets");

interface NamespaceEventsMap {
  drivers: {
    serverToClient: DriverServerToClientEvents;
    clientToServer: DriverClientToServerEvents;
  };
  chats: {
    serverToClient: ChatServerToClientEvents;
    clientToServer: ChatClientToServerEvents;
  };
}

type SocketNamespaces = keyof NamespaceEventsMap;

type NamespaceSocket<T extends SocketNamespaces> = Socket<
  NamespaceEventsMap[T]["serverToClient"],
  NamespaceEventsMap[T]["clientToServer"]
>;

class WebSocketClient {
  private sockets: Map<string, Socket> = new Map();
  private initializingNamespaces: Set<string> = new Set();

  public async initializeSocket<T extends SocketNamespaces>(
    namespace: T,
  ): Promise<NamespaceSocket<T> | undefined> {
    if (this.sockets.has(namespace)) {
      return this.sockets.get(namespace) as NamespaceSocket<T>;
    }

    if (this.initializingNamespaces.has(namespace)) {
      return;
    }

    this.initializingNamespaces.add(namespace);

    try {
      const at = await authService?.getAccessToken();
      if (!at) {
        return;
      }

      const socket = this.createWebSocketClient<T>(namespace, at);
      socket.connect();

      this.sockets.set(namespace, socket);
      return socket;
    } finally {
      this.initializingNamespaces.delete(namespace);
    }
  }

  private createWebSocketClient<T extends SocketNamespaces>(
    namespace: T,
    accessToken: string | undefined,
  ): NamespaceSocket<T> {
    const socket = io(`${config.API_BASE_URL}/${namespace}`, {
      transports: ["websocket"],
      autoConnect: true,
      reconnection: true,
      auth: {
        token: accessToken,
      },
    });

    return socket as NamespaceSocket<T>;
  }

  public getSocket<T extends SocketNamespaces>(
    namespace: T,
  ): NamespaceSocket<T> | undefined {
    return this.sockets.get(namespace) as NamespaceSocket<T> | undefined;
  }

  public disconnect(namespace?: string) {
    if (namespace) {
      const socket = this.sockets.get(namespace);
      if (socket) {
        socket.disconnect();
        this.sockets.delete(namespace);
      }
    } else {
      this.sockets.forEach((socket) => socket.disconnect());
      this.sockets.clear();
    }
  }

  public disconnectAll() {
    this.disconnect();
  }
}

export const webSocketClient = new WebSocketClient();
